import type { ReactElement } from "react"
import React, { useMemo } from "react"

import { useTranslation } from "react-i18next"

import Grid from "@mui/material/Grid"
import Skeleton from "@mui/material/Skeleton"
import type { BarDatum } from "@nivo/bar"
import { ResponsiveBar } from "@nivo/bar"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import { annotateNumber, customColorScheme } from "../../../utils/genericHelpers"
import { KonanEmptyState } from "../../utility/KonanEmptyState"
import type { ChartsContainerProps } from "../ChartsContainer"
import { ChartsContainer } from "../ChartsContainer"

interface BaseBarProps extends Omit<ChartsContainerProps, "children" | "emptyState"> {
  title: string
  data: BarDatum[]
  isLoading: boolean
  graphHeight: number
  subHeader?: ReactElement
  footer?: ReactElement
  indexBy: string
  keys?: string[]
  emptyState?: { title: string; subTitle?: string }
  groupMode?: "grouped" | "stacked"
}

export function BaseBarChart(props: Readonly<BaseBarProps>): React.ReactElement {
  const { t } = useTranslation("graphs")

  const {
    title,
    data,
    isLoading,
    graphHeight,
    subHeader,
    footer,
    indexBy,
    keys,
    emptyState,
    selectProps,
    customHeaderComponent,
    groupMode = "grouped",
    tooltipText,
  } = props

  const theme = getTheme()

  const adjustedHeight = useMemo((): number => {
    let height = graphHeight

    if (subHeader) height += 70
    if (footer) height += 70

    return height
  }, [footer, graphHeight, subHeader])

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={adjustedHeight}
      selectProps={selectProps}
      customHeaderComponent={customHeaderComponent}
      tooltipText={tooltipText}
    >
      {emptyState ? (
        <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
          <KonanEmptyState
            title={emptyState?.title ?? t("baseBarChart.emptyState.topFeaturesUnavailable")}
            subTitle={emptyState?.subTitle}
          />
        </Grid>
      ) : (
        <Grid container py={2} gap={1}>
          <Grid item xs={12}>
            {subHeader}
          </Grid>

          <Grid item xs={12} style={{ height: graphHeight, padding: "4px 16px 8px 16px" }}>
            {(!data || data.length < 1) && !isLoading ? (
              <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
                <Typography variant="h2-bold">{t("baseBarChart.emptyState.noDataToDisplay")}</Typography>
              </Grid>
            ) : isLoading || !data ? (
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="flex-start"
                className={"full-height"}
              >
                <Skeleton animation="wave" width="100%" height={40} />
                <Skeleton animation="wave" width="75%" />
                <Skeleton animation="wave" width="60%" height={30} />
                <Skeleton animation="wave" width="30%" />
              </Grid>
            ) : (
              <ResponsiveBar
                data={data}
                theme={NivoTheme(theme)}
                indexBy={indexBy}
                keys={keys}
                colors={customColorScheme()}
                colorBy="id"
                margin={{ top: 10, right: 0, bottom: 40, left: 50 }}
                borderRadius={4}
                padding={0.4}
                layout="vertical"
                groupMode={groupMode}
                valueScale={{ type: "linear" }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legendOffset: 0,
                  truncateTickAt: 0,
                }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: t("baseBarChart.axis.count"),
                  legendOffset: -40,
                  legendPosition: "middle",
                  truncateTickAt: 0,
                  format: (tick) => annotateNumber(tick, 1),
                }}
                valueFormat={(v) => v.toFixed(2)}
                enableLabel
                enableGridY
                enableGridX
                labelSkipHeight={12}
                labelTextColor={{ from: "color", modifiers: [["brighter", 3]] }}
                animate={true}
                labelSkipWidth={2000} // labelSkipWidth = minimum bar size needed for a normal label
                tooltip={(slice) => (
                  <div className={"chart-tooltip"}>
                    <Typography variant="h3-bold" variantColor={2}>
                      {slice.data.label}
                    </Typography>

                    <Typography variant="p" variantColor={2}>
                      {t("baseBarChart.tooltip.value", { id: slice.id ?? 0, value: slice.value ?? 0 })}
                    </Typography>
                  </div>
                )}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            {footer}
          </Grid>
        </Grid>
      )}
    </ChartsContainer>
  )
}
